<script setup lang="ts">
import { formatLocalDate, parseLocalDate } from '@taler/domain'
import { Select } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RowView } from '@/features/entries'
import type {
  PrepaidScheduleDraft,
  PrepaidScheduleMatch,
} from '../composables/use-prepaid-schedule-extension.ts'

const props = defineProps<{ match: PrepaidScheduleMatch; draft: PrepaidScheduleDraft; view: RowView }>()
const emit = defineEmits<{ 'update:draft': [PrepaidScheduleDraft] }>()

const { t } = useI18n()

const LABEL_CLASS = 'text-[length:var(--text-step-2)] text-[color:var(--color-text)]'
const INPUT_CLASS =
  'min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]'

const expenseOptions = computed(() =>
  props.match.expenseAccounts.map((account) => ({ value: account.id, label: account.label })),
)
const startDate = computed(() => formatLocalDate(props.draft.startDate ?? props.view.row.date))

function update(patch: Partial<PrepaidScheduleDraft>): void {
  emit('update:draft', { ...props.draft, ...patch })
}

function onStartDate(value: string): void {
  if (value !== '') update({ startDate: parseLocalDate(value) })
}
</script>

<template>
  <div class="flex flex-wrap items-end gap-[var(--space-cell)]">
    <label class="flex min-w-60 flex-col gap-[var(--space-hair)]">
      <span :class="LABEL_CLASS">{{ t('prepaidRecognitions.expenseAccountLabel') }}</span>
      <Select
        :model-value="draft.expenseAccountId"
        :options="expenseOptions"
        :placeholder="t('prepaidRecognitions.selectExpenseAccount')"
        :aria-label="t('prepaidRecognitions.expenseAccountLabel')"
        @update:model-value="(expenseAccountId: string) => update({ expenseAccountId })"
      />
    </label>
    <label class="flex flex-col gap-[var(--space-hair)]">
      <span :class="LABEL_CLASS">{{ t('prepaidRecognitions.periodsLabel') }}</span>
      <input
        :value="draft.periods"
        type="number"
        min="1"
        step="1"
        :class="[INPUT_CLASS, 'w-24']"
        @input="(event) => update({ periods: (event.target as HTMLInputElement).value })"
      />
    </label>
    <label class="flex flex-col gap-[var(--space-hair)]">
      <span :class="LABEL_CLASS">{{ t('prepaidRecognitions.startDateLabel') }}</span>
      <input
        :value="startDate"
        type="date"
        :class="INPUT_CLASS"
        @change="(event) => onStartDate((event.target as HTMLInputElement).value)"
      />
    </label>
  </div>
</template>
